import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { TicketProvisioningService } from 'src/app/services/ticket-provisioning.service';
import { AvdStatusPollingService } from 'src/app/services/avd-status-polling.service';
import { ProvisioningStatusResponse, ConnectionDetailsResponse } from 'src/app/interface/avd-provisioning';
import { NzMessageService } from 'ng-zorro-antd/message';

/**
 * Component to display AVD provisioning status for a ticket
 * Shows real-time status updates and connection details when available
 */
@Component({
  selector: 'app-avd-provisioning-status',
  templateUrl: './avd-provisioning-status.component.html',
  styleUrls: ['./avd-provisioning-status.component.css']
})
export class AvdProvisioningStatusComponent implements OnInit, OnDestroy {
  @Input() ticketId!: string;
  @Input() autoRefresh: boolean = true; // Enable auto-refresh by default

  provisioningStatus: ProvisioningStatusResponse | null = null;
  connectionDetails: ConnectionDetailsResponse | null = null;
  isLoading: boolean = false;
  isLoadingConnection: boolean = false;
  isRetrying: boolean = false;
  error: string | null = null;

  private pollingSubscription?: Subscription;

  constructor(
    private provisioningService: TicketProvisioningService,
    private pollingService: AvdStatusPollingService,
    private message: NzMessageService
  ) { }

  ngOnInit(): void {
    if (!this.ticketId) {
      console.error('[AVD Status] No ticket ID provided');
      return;
    }

    console.log(`[AVD Status] Initializing for ticket: ${this.ticketId}`);
    this.loadProvisioningStatus();

    // Start auto-refresh if enabled
    if (this.autoRefresh) {
      this.startPolling();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  /**
   * Load current provisioning status
   */
  loadProvisioningStatus(): void {
    this.isLoading = true;
    this.error = null;

    this.provisioningService.getProvisioningStatus(this.ticketId).subscribe({
      next: (status) => {
        this.provisioningStatus = status;
        this.isLoading = false;

        // Load connection details if provisioning is successful
        if (status.status === 'Success' && status.hasConnectionDetails) {
          this.loadConnectionDetails();
        }
      },
      error: (error) => {
        // Handle different types of errors gracefully
        if (error.status === 404) {
          this.error = 'VDI provisioning information not found for this ticket. This ticket may not be a VDI setup ticket.';
        } else if (error.status === 403) {
          this.error = 'You do not have permission to view the provisioning status for this ticket.';
        } else if (error.status === 500) {
          this.error = 'VDI provisioning service is currently unavailable. Please try again later.';
        } else {
          this.error = error.message || 'Failed to load provisioning status';
        }
        this.isLoading = false;
        console.error('[AVD Status] Error loading status:', error);
      }
    });
  }

  /**
   * Load connection details for successful provisioning
   */
  loadConnectionDetails(): void {
    this.isLoadingConnection = true;

    this.provisioningService.getConnectionDetails(this.ticketId).subscribe({
      next: (details) => {
        this.connectionDetails = details;
        this.isLoadingConnection = false;
      },
      error: (error) => {
        console.error('[AVD Status] Error loading connection details:', error);
        this.isLoadingConnection = false;
        // Don't set error state as this is not critical
      }
    });
  }

  /**
   * Start polling for status updates
   */
  startPolling(): void {
    if (this.pollingSubscription) {
      return; // Already polling
    }

    this.pollingSubscription = this.pollingService.startPolling(
      this.ticketId,
      {
        intervalMs: 30000, // 30 seconds
        stopOnSuccess: true // Stop when provisioning succeeds
      }
    ).subscribe({
      next: (status) => {
        if (status) {
          this.provisioningStatus = status;

          // Load connection details if just succeeded
          if (status.status === 'Success' && status.hasConnectionDetails && !this.connectionDetails) {
            this.loadConnectionDetails();
          }
        }
      },
      error: (error) => {
        console.error('[AVD Status] Polling error:', error);
      }
    });
  }

  /**
   * Stop polling
   */
  stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = undefined;
    }
    this.pollingService.stopPolling(this.ticketId);
  }

  /**
   * Retry failed provisioning
   */
  retryProvisioning(): void {
    this.isRetrying = true;
    this.error = null;

    this.provisioningService.retryProvisioning(this.ticketId).subscribe({
      next: (response) => {
        this.message.success('Provisioning retry initiated successfully');
        this.isRetrying = false;
        
        // Refresh status
        this.loadProvisioningStatus();
        
        // Restart polling
        this.stopPolling();
        if (this.autoRefresh) {
          this.startPolling();
        }
      },
      error: (error) => {
        this.error = error.message || 'Failed to retry provisioning';
        this.isRetrying = false;
        this.message.error(this.error || 'An error occurred');
        console.error('[AVD Status] Retry error:', error);
      }
    });
  }

  /**
   * Manually refresh status
   */
  refreshStatus(): void {
    this.loadProvisioningStatus();
    
    if (this.pollingService.isPolling(this.ticketId)) {
      this.pollingService.refreshStatus(this.ticketId);
    }
  }

  /**
   * Copy connection link to clipboard
   */
  copyConnectionLink(): void {
    if (!this.connectionDetails?.connectionLink) {
      return;
    }

    navigator.clipboard.writeText(this.connectionDetails.connectionLink).then(() => {
      this.message.success('Connection link copied to clipboard');
    }).catch(error => {
      console.error('[AVD Status] Copy error:', error);
      this.message.error('Failed to copy connection link');
    });
  }

  /**
   * Copy credentials to clipboard
   */
  copyCredentials(): void {
    if (!this.connectionDetails) {
      return;
    }

    const credentials = `Username: ${this.connectionDetails.temporaryUsername}\nPassword: ${this.connectionDetails.temporaryPassword}`;
    
    navigator.clipboard.writeText(credentials).then(() => {
      this.message.success('Credentials copied to clipboard');
    }).catch(error => {
      console.error('[AVD Status] Copy error:', error);
      this.message.error('Failed to copy credentials');
    });
  }

  /**
   * Get status badge color
   */
  getStatusColor(): string {
    if (!this.provisioningStatus) return 'default';
    return this.provisioningService.getStatusColor(this.provisioningStatus.status);
  }

  /**
   * Get status message
   */
  getStatusMessage(): string {
    if (!this.provisioningStatus) return '';
    return this.provisioningService.getStatusMessage(this.provisioningStatus.status);
  }

  /**
   * Check if credentials are expiring soon
   */
  areCredentialsExpiringSoon(): boolean {
    if (!this.connectionDetails) return false;
    return this.provisioningService.areCredentialsExpiringSoon(this.connectionDetails.credentialExpiresUtc);
  }

  /**
   * Check if credentials are expired
   */
  areCredentialsExpired(): boolean {
    if (!this.connectionDetails) return false;
    return this.provisioningService.areCredentialsExpired(this.connectionDetails.credentialExpiresUtc);
  }

  /**
   * Format date for display
   */
  formatDate(date: string | undefined): string {
    return this.provisioningService.formatDate(date);
  }

  /**
   * Get status icon
   */
  getStatusIcon(): string {
    if (!this.provisioningStatus) return 'clock-circle';
    
    switch (this.provisioningStatus.status) {
      case 'Pending':
        return 'clock-circle';
      case 'InProgress':
        return 'loading';
      case 'Success':
        return 'check-circle';
      case 'Failed':
        return 'close-circle';
      default:
        return 'question-circle';
    }
  }
}
